import { shouldIgnoreUnauthorized, suppressStaleUnauthorized } from '../../functions/staleUnauthorized'

export type SupportRecord = Record<string, unknown>

export function pick<T>(row: SupportRecord | null | undefined, ...keys: string[]): T | undefined {
  if (!row) return undefined
  for (const key of keys) {
    const value = row[key]
    if (value !== undefined && value !== null) return value as T
  }
  return undefined
}

export function authHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`
  }
}

let unauthorizedHandler: (() => void) | null = null

/** SupportHeader registers this so an expired support session logs out and goes back to /support-login. */
export function setSupportUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler
}

export async function supportRequest<T>(baseUrl: string, path: string, options: RequestInit = {}): Promise<T> {
  const requestToken = localStorage.getItem('token')
  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: { ...authHeaders(), ...(options.headers || {}) }
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    // A 401 sent with a token that was replaced a moment ago (company switch) is not a real expiry.
    if (response.status === 401 && !shouldIgnoreUnauthorized(requestToken)) {
      if (unauthorizedHandler) unauthorizedHandler()
      throw new Error('Your support session ended. Please sign in again.')
    }
    const message = (data && (data.message || data.Message)) || 'Request failed.'
    throw new Error(message)
  }
  return data as T
}

const RETURN_KEY = 'supportReturnTo'

/** Remembers the support screen a connect started from, so Exit on the impersonation banner goes back there. */
export function rememberSupportReturn(path: string): void {
  localStorage.setItem(RETURN_KEY, path)
}

/** The screen to go back to after Exit (Connect when unknown); cleared once read. */
export function takeSupportReturn(): string {
  const path = localStorage.getItem(RETURN_KEY) || ''
  localStorage.removeItem(RETURN_KEY)
  return path.startsWith('/support/') ? path : '/support/companies'
}

export function pickItems(data: SupportRecord): unknown[] {
  const items = data.items || data.Items
  return Array.isArray(items) ? items : []
}

/** Date (or date and time) for display; blank for empty values and placeholder dates before 1900. */
export function formatWhen(value: string, withTime = false): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime()) || date.getFullYear() < 1900) return ''
  return withTime ? date.toLocaleString() : date.toLocaleDateString()
}

export interface SupportSessionExtras {
  companyName?: string
  userName?: string
  supportUserName?: string
  supportDisplayName?: string
}

const isTrue = (value: unknown) => String(value || '').toLowerCase() === 'true'

/**
 * Payload for authActions.supportSession from a support login, MFA, select-company, impersonate
 * or exit-impersonate response. Requests still in flight carry the old token, so their 401s are
 * ignored for a few seconds.
 */
export function supportSessionFromToken(data: SupportRecord, extras: SupportSessionExtras = {}) {
  suppressStaleUnauthorized()
  const supportUserName = data.supportUserName || data.SupportUserName || extras.supportUserName || data.supportDisplayName
  return {
    token: data.access_token,
    duration: data.expires_in || data.expiresIn || 14400,
    role: data.role || data.Role || 'support',
    supportUserName,
    supportDisplayName: data.supportDisplayName || data.SupportDisplayName || extras.supportDisplayName || supportUserName,
    companyName: data.companyName || data.CompanyName || extras.companyName || '',
    isImpersonating: isTrue(data.isImpersonating),
    isAdmin: isTrue(data.isAdmin || data.IsAdmin),
    canSelectCompany: isTrue(data.canSelectCompany || data.CanSelectCompany),
    impersonatedUserName: data.impersonatedUserName || '',
    addPermission: data.addPermission,
    updatePermission: data.updatePermission,
    deletePermission: data.deletePermission,
    hub: data.hub,
    allowChargeCreditCard: data.allowChargeCreditCard,
    inTrial: data.inTrial,
    trialEndDate: data.trialEndDate,
    hasJiraCredentials: data.hasJiraCredentials,
    startupPage: data.startupPage,
    userName: extras.userName || data.impersonatedUserName || supportUserName
  }
}

export interface SupportUpdate {
  id: number
  clientId: number
  clientName: string
  updateInfo: string
  versionFrom: number
  versionToId: number
  versionToNo: number
  serviceUpdateNeeded: boolean
  notes: string
  ticketInfo: string
  dateAdded: string
  dateUpdated: string
  updated: boolean
  versionReleased: boolean
  computers: SupportUpdateComputer[]
}

export interface SupportUpdateComputer {
  id: number
  name: string
  checked: boolean
}

export function readUpdate(row: SupportRecord): SupportUpdate {
  return {
    id: Number(pick(row, 'id', 'Id') || 0),
    clientId: Number(pick(row, 'clientId', 'ClientId') || 0),
    clientName: String(pick(row, 'clientName', 'ClientName') || ''),
    updateInfo: String(pick(row, 'updateInfo', 'UpdateInfo') || ''),
    versionFrom: Number(pick(row, 'versionFrom', 'VersionFrom') || 0),
    versionToId: Number(pick(row, 'versionToId', 'VersionToId') || 0),
    versionToNo: Number(pick(row, 'versionToNo', 'VersionToNo') || 0),
    serviceUpdateNeeded: Boolean(pick(row, 'serviceUpdateNeeded', 'ServiceUpdateNeeded')),
    notes: String(pick(row, 'notes', 'Notes') || ''),
    ticketInfo: String(pick(row, 'ticketInfo', 'TicketInfo') || ''),
    dateAdded: String(pick(row, 'dateAdded', 'DateAdded') || ''),
    dateUpdated: String(pick(row, 'dateUpdated', 'DateUpdated') || ''),
    updated: Boolean(pick(row, 'updated', 'Updated')),
    versionReleased: Boolean(pick(row, 'versionReleased', 'VersionReleased')),
    computers: (((row.computers || row.Computers) as SupportRecord[] | undefined) || []).map(readComputer)
  }
}

function readComputer(row: SupportRecord): SupportUpdateComputer {
  return {
    id: Number(pick(row, 'id', 'Id') || 0),
    name: String(pick(row, 'name', 'Name') || ''),
    checked: Boolean(pick(row, 'checked', 'Checked'))
  }
}

/**
 * Copies text to the clipboard. Returns false when the browser has no clipboard API or refuses
 * (not https, permission denied), so the caller can show the text to copy by hand.
 */
export async function copyText(text: string): Promise<boolean> {
  if (!navigator.clipboard || !navigator.clipboard.writeText) return false
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (e) {
    return false
  }
}

/**
 * Last version released by hand. Versions after it are released from this screen, which uses
 * their Dropbox download link, so the Release button only shows for them.
 */
export const LAST_MANUAL_RELEASE_VERSION = 1629

/** Lowest valid "Version from"; smaller numbers are not real ADS versions. */
export const MIN_VERSION_FROM = 100

export interface SupportVersion {
  id: number
  versionNo: number
  serviceUpdateNeeded: boolean
  messageForUser: string
  notes: string
  released: boolean
  dateReleased: string
  link: string
}

export function readVersion(row: SupportRecord): SupportVersion {
  return {
    id: Number(pick(row, 'id', 'Id') || 0),
    versionNo: Number(pick(row, 'versionNo', 'VersionNo') || 0),
    serviceUpdateNeeded: Boolean(pick(row, 'serviceUpdateNeeded', 'ServiceUpdateNeeded')),
    messageForUser: String(pick(row, 'messageForUser', 'MessageForUser') || ''),
    notes: String(pick(row, 'notes', 'Notes') || ''),
    released: Boolean(pick(row, 'released', 'Released')),
    dateReleased: String(pick(row, 'dateReleased', 'DateReleased') || ''),
    link: String(pick(row, 'link', 'Link') || '')
  }
}
